// Admin "Policies" authoring surface strings — EN + TR (Phase 5A UI polish).

import type { LocalizedDict } from '../index'

type PoliciesStrings = {
  title: string
  writtenPolicies: string
  writtenPoliciesHint: string
  moreActions: string
  addPolicy: string
  statusPublished: string
  statusDraft: string
  statusNone: string
  statusNew: string
  version: string
  controlling: string
  controllingDisclaimer: string
  contentHtml: string
  manageAudience: string
  saveFirstForAudience: string
  reacceptRequired: string
  cancel: string
  saveDraft: string
  createNewVersion: string
  publish: string
  missing: string
  publishGateHint: string
  nameLabel: string
  namePlaceholder: string
  nameRequired: string
  audienceHint: string
  requiredFor: string
  resellers: string
  resellersHint: string
  vendors: string
  vendorsHint: string
  specificOrgs: string
  specificOrgsHint: string
  exemptOrgs: string
  exemptOrgsHint: string
  save: string
  audienceNone: string
  publishTitle: string
  publishSubtitle: string
  languages: string
  yes: string
  no: string
  audience: string
  affectedOrgs: string
  confirmPublish: string
  rowActions: string
  deleteDraftPolicy: string
  deleteTitle: string
  deleteBody: string
  deleteHint: string
  deleteConfirm: string
}

export const policiesI18n: LocalizedDict<PoliciesStrings> = {
  en: {
    title: 'Policies',
    writtenPolicies: 'Written policies',
    writtenPoliciesHint:
      'The Terms & Conditions sellers and vendors accept. English is the controlling version; Türkçe is a translation of the same agreement.',
    moreActions: 'More actions',
    addPolicy: 'Add policy',
    statusPublished: 'Published',
    statusDraft: 'Draft',
    statusNone: 'No policy set',
    statusNew: 'New',
    version: 'Version',
    controlling: '· controlling',
    controllingDisclaimer:
      'English is the controlling version. Türkçe is a translation of the same agreement — keep both in sync before publishing. No governing law is set here.',
    contentHtml: 'Content',
    manageAudience: 'Manage audience',
    saveFirstForAudience: 'Save a draft first to manage audience',
    reacceptRequired: 'Re-acceptance required',
    cancel: 'Cancel',
    saveDraft: 'Save draft',
    createNewVersion: 'Create new version',
    publish: 'Publish',
    missing: 'Missing',
    publishGateHint: 'Both languages must be complete before this version can be published.',
    nameLabel: 'Policy name',
    namePlaceholder: 'Policy name (e.g. Reseller Terms)',
    nameRequired: 'A policy name is required.',
    audienceHint: 'Choose which organizations must accept this policy.',
    requiredFor: 'Required for',
    resellers: 'Resellers',
    resellersHint: '(buy-side sellers)',
    vendors: 'Vendors',
    vendorsHint: '(supply-side)',
    specificOrgs: 'Specific organizations',
    specificOrgsHint: 'Require this policy for individual organizations by id.',
    exemptOrgs: 'Exempt organizations',
    exemptOrgsHint: 'Never require this policy for these organizations.',
    save: 'Save',
    audienceNone: 'No one yet',
    publishTitle: 'Publish this version?',
    publishSubtitle: 'Once published, this version becomes effective and can no longer be edited.',
    languages: 'Languages',
    yes: 'Yes',
    no: 'No',
    audience: 'Audience',
    affectedOrgs: 'Affected organizations',
    confirmPublish: 'Publish version',
    rowActions: 'Row actions',
    deleteDraftPolicy: 'Delete draft policy',
    deleteTitle: 'Delete draft policy?',
    deleteBody: 'This permanently removes the draft policy',
    deleteHint: 'Only an unpublished draft with no audience can be deleted. This cannot be undone.',
    deleteConfirm: 'Delete draft',
  },
  tr: {
    title: 'Sözleşmeler',
    writtenPolicies: 'Yazılı sözleşmeler',
    writtenPoliciesHint:
      'Satıcı ve tedarikçilerin kabul ettiği Şartlar & Koşullar. İngilizce geçerli (controlling) sürümdür; Türkçe aynı sözleşmenin çevirisidir.',
    moreActions: 'Diğer işlemler',
    addPolicy: 'Sözleşme ekle',
    statusPublished: 'Yayında',
    statusDraft: 'Taslak',
    statusNone: 'Sözleşme yok',
    statusNew: 'Yeni',
    version: 'Sürüm',
    controlling: '· geçerli',
    controllingDisclaimer:
      'İngilizce geçerli (controlling) sürümdür. Türkçe aynı sözleşmenin çevirisidir — yayınlamadan önce ikisini eşitleyin. Burada geçerli hukuk (governing law) belirlenmez.',
    contentHtml: 'İçerik',
    manageAudience: 'Kitleyi yönet',
    saveFirstForAudience: 'Kitleyi yönetmek için önce taslak kaydedin',
    reacceptRequired: 'Yeniden kabul gerekli',
    cancel: 'Vazgeç',
    saveDraft: 'Taslağı kaydet',
    createNewVersion: 'Yeni sürüm oluştur',
    publish: 'Yayınla',
    missing: 'Eksik',
    publishGateHint: 'Bu sürüm yayınlanmadan önce iki dil de tamamlanmalı.',
    nameLabel: 'Sözleşme adı',
    namePlaceholder: 'Sözleşme adı (örn. Bayi Şartları)',
    nameRequired: 'Sözleşme adı gerekli.',
    audienceHint: 'Bu sözleşmeyi hangi kuruluşların kabul etmesi gerektiğini seçin.',
    requiredFor: 'Şunlar için gerekli',
    resellers: 'Bayiler',
    resellersHint: '(satın-alma tarafı satıcılar)',
    vendors: 'Tedarikçiler',
    vendorsHint: '(tedarik tarafı)',
    specificOrgs: 'Belirli kuruluşlar',
    specificOrgsHint: 'Bu sözleşmeyi tek tek kuruluşlar için id ile zorunlu kılın.',
    exemptOrgs: 'Muaf kuruluşlar',
    exemptOrgsHint: 'Bu kuruluşlar için sözleşmeyi asla zorunlu kılma.',
    save: 'Kaydet',
    audienceNone: 'Henüz kimse',
    publishTitle: 'Bu sürüm yayınlansın mı?',
    publishSubtitle: 'Yayınlandıktan sonra bu sürüm yürürlüğe girer ve artık düzenlenemez.',
    languages: 'Diller',
    yes: 'Evet',
    no: 'Hayır',
    audience: 'Kitle',
    affectedOrgs: 'Etkilenecek kuruluşlar',
    confirmPublish: 'Sürümü yayınla',
    rowActions: 'Satır işlemleri',
    deleteDraftPolicy: 'Taslak sözleşmeyi sil',
    deleteTitle: 'Taslak sözleşme silinsin mi?',
    deleteBody: 'Bu, şu taslak sözleşmeyi kalıcı olarak siler:',
    deleteHint: 'Yalnız yayınlanmamış, kitlesi olmayan bir taslak silinebilir. Bu işlem geri alınamaz.',
    deleteConfirm: 'Taslağı sil',
  },
}
